/**
 * Scroll-lock safety net — CUSTOM PATCH (not part of the Cornerstone theme).
 * ---------------------------------------------------------------------------
 * Cornerstone disables page scrolling by setting `document.body.style.overflow
 * = 'hidden'` whenever a modal-type overlay opens (the timed popup, mobile menu,
 * quick-cart, quick-view / product modal, filter drawer, quick-search). It does
 * this through a single, reference-counted lock (the `body-scroll-lock` library,
 * see the shared `locks` array in theme.js). `overflow: hidden` is only restored
 * once that counter returns to zero.
 *
 * If any overlay is removed or re-rendered before its matching release runs —
 * most commonly the timed popup, whose release is deferred by 500ms — the
 * counter never empties and `overflow: hidden` is left on <body>, freezing the
 * whole page ("after some time I can't scroll").
 *
 * This watchdog only ever acts when the page is *wrongly* locked: body scroll is
 * disabled while NO overlay is actually open. When a real overlay is visible it
 * does nothing, so the popup and every other overlay behave exactly as before.
 */
(function () {
  "use strict";

  // The page must stay wrongly-locked for at least this long before we release,
  // so we never race a legitimate overlay's own close animation / deferred
  // release (the popup releases on a 500ms timer).
  var STUCK_GRACE_MS = 800;
  var POLL_MS = 500;

  var lockedSince = null;

  function bodyScrollIsLocked() {
    // body-scroll-lock sets this on <body>; we also check <html> defensively.
    return (
      document.body.style.overflow === "hidden" ||
      document.documentElement.style.overflow === "hidden"
    );
  }

  function isVisible(el) {
    if (!el) return false;
    var style = window.getComputedStyle(el);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      parseFloat(style.opacity) === 0
    ) {
      return false;
    }
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  // Returns true when an overlay is genuinely on screen. Uses the reliable
  // markers the theme sets on <body>, the popup's own visible-state class, and a
  // conservative backstop for any other large fixed overlay (filter drawer /
  // quick-search) so we never unlock the page behind something that is open.
  function anOverlayIsOpen() {
    var body = document.body;

    // Mobile menu.
    if (body.getAttribute("mobile-menu-open") === "true") return true;
    // Quick-cart and quick-view / product modals.
    if (body.getAttribute("data-drawer-modal-open") === "true") return true;

    // Centered popup / age gate: the [data-popup] container gains `.visible`.
    var popup = document.querySelector("[data-popup].visible");
    if (popup && isVisible(popup)) return true;

    // Backstop: any currently-visible overlay-like element that covers most of
    // the viewport height (catches overlays not tracked above). Sized so it can
    // never match the sticky header, banners, or back-to-top button.
    var viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    var candidates = document.querySelectorAll(
      '[aria-modal="true"], .drawer, .modal, .popup, .quick-search, ' +
        ".filter-drawer, [data-drawer], [data-modal]"
    );
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (!isVisible(el)) continue;
      if (el.getBoundingClientRect().height >= viewportHeight * 0.5) return true;
    }

    return false;
  }

  function releaseStuckLock() {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.documentElement.style.overflow = "";
    lockedSince = null;
  }

  function evaluate() {
    if (bodyScrollIsLocked() && !anOverlayIsOpen()) {
      var now = new Date().getTime();
      if (lockedSince === null) lockedSince = now;
      if (now - lockedSince >= STUCK_GRACE_MS) releaseStuckLock();
    } else {
      lockedSince = null;
    }
  }

  function start() {
    window.setInterval(evaluate, POLL_MS);
    // Re-check the moment the shopper tries to scroll, so a genuinely stuck page
    // unfreezes on their first scroll attempt rather than waiting for the poll.
    ["wheel", "touchmove", "keydown"].forEach(function (type) {
      window.addEventListener(type, evaluate, { passive: true });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
